/**
 * Download a video file to disk. Asks the API for a short-lived presigned URL
 * with Content-Disposition: attachment (works for both the public AI bucket and
 * the private user bucket), then triggers the browser download.
 *
 * Auth stays in the Bearer header; the returned presigned URL carries its own
 * signature, so the anchor navigation needs no credentials.
 */
export async function downloadVideoFile(
  videoId: string,
  token?: string | null,
): Promise<void> {
  const res = await fetch(`/api/videos/${videoId}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let message = `Download failed (HTTP ${res.status})`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  const { url, filename } = (await res.json()) as { url: string; filename?: string };
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename; // cross-origin ignores this; the signed
  // Content-Disposition header forces the download + name regardless.
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Download an arbitrary remote video URL (e.g. a curated example clip that isn't
 * in our DB) to disk. The example CDNs don't send CORS headers, so a direct
 * blob-fetch fails — instead we route through our own same-origin proxy
 * (/api/videos/download-proxy) which streams the file back with an attachment
 * header. Falls back to opening the URL in a new tab if the proxy fails.
 */
export async function downloadFromUrl(url: string, filename: string): Promise<void> {
  try {
    const token =
      typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    const proxied = `/api/videos/download-proxy?url=${encodeURIComponent(url)}&name=${encodeURIComponent(filename)}`;
    const res = await fetch(proxied, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick so the download has started.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
  } catch {
    // Proxy/network error → best-effort: open in a new tab.
    window.open(url, '_blank', 'noopener');
  }
}
